import {
  DEFAULT_INSIGHTS,
  DEFAULT_NOTIFICATIONS,
} from '~/constants/notifications'

export default function useNotificationsData() {
  const { $rentals, $captureError } = useNuxtApp()

  /**
   * Notifications.
   *
   * Granular notifications are used to tell the user which areas need action.
   * Examples include unread messages in their inbox, and host opportunities.
   */
  const { isHostMode } = useBaseName()
  const notifications = useNotifications()

  const bookingNotificationsGrouped = computed(() => notifications.value.Counts.Booking?.Grouped ?? 0)
  const bookingNotificationsTotal = computed(() => notifications.value.Counts.Booking?.Total ?? 0)

  const bookingDetailsNotificationsGrouped = computed(() => notifications.value.Counts.BookingDetails?.Grouped ?? 0)
  const bookingDetailsNotificationsTotal = computed(() => notifications.value.Counts.BookingDetails?.Total ?? 0)

  const dashboardNotificationsGrouped = computed(() => notifications.value.Counts.Dashboard?.Grouped ?? 0)
  const dashboardNotificationsTotal = computed(() => notifications.value.Counts.Dashboard?.Total ?? 0)

  const inboxNotificationsGrouped = computed(() => notifications.value.Counts.Inbox?.Grouped ?? 0)
  const inboxNotificationsTotal = computed(() => notifications.value.Counts.Inbox?.Total ?? 0)

  const opportunitiesNotificationsGrouped = computed(() => notifications.value.Counts.Opportunities?.Grouped ?? 0)
  const opportunitiesNotificationsTotal = computed(() => notifications.value.Counts.Opportunities?.Total ?? 0)

  const stepsNotificationsGrouped = computed(() => notifications.value.Counts.Steps?.Grouped ?? 0)
  const stepsNotificationsTotal = computed(() => notifications.value.Counts.Steps?.Total ?? 0)

  /**
   * Fetches granular notifications from the API, based on the current host
   * mode.
   */
  function fetchNotifications() {
    return $rentals('/api/notifications/indicator', {
      query: {
        mode: isHostMode.value ? 'Host' : 'Guest',
      },
    })
  }

  /**
   * Sets the notifications by fetching them.
   */
  async function getAndSetNotifications() {
    try {
      notifications.value = await fetchNotifications()
    }
    catch (error) {
      $captureError(error)
    }
  }

  /**
   * Resets all notifications.
   */
  function resetNotifications() {
    notifications.value = DEFAULT_NOTIFICATIONS
  }

  /**
   * Insights.
   *
   * The user's Inisight's data.
   */
  const insights = useInsights()

  const isSuspended = computed(() => insights.value.PerformanceTier === 'ActionRequired')

  /**
   * Fetches insights data from the API.
   */
  function fetchInsights() {
    return $rentals('/api/hosts/insights')
  }

  /**
   * Fetches and sets insights data.
   */
  async function getAndSetInsights() {
    try {
      insights.value = await fetchInsights()
    }
    catch (error) {
      $captureError(error)
    }
  }

  function resetInsights() {
    insights.value = DEFAULT_INSIGHTS
  }

  return {
    notifications,

    bookingNotificationsGrouped,
    bookingNotificationsTotal,

    bookingDetailsNotificationsGrouped,
    bookingDetailsNotificationsTotal,

    dashboardNotificationsGrouped,
    dashboardNotificationsTotal,

    inboxNotificationsGrouped,
    inboxNotificationsTotal,

    opportunitiesNotificationsGrouped,
    opportunitiesNotificationsTotal,

    stepsNotificationsGrouped,
    stepsNotificationsTotal,

    fetchNotifications,
    getAndSetNotifications,
    resetNotifications,

    insights,
    isSuspended,

    fetchInsights,
    getAndSetInsights,
    resetInsights,
  }
}
